import classNames from "classnames";
import { useEffect, useRef, useState } from "react";

import useOnClickOutside from "../../../hooks/useClickOutside";
import useClientPosition from "../../../hooks/useClientPosition";
import { hideModal, setModalType } from "../../../store/slices/modalSlice";
import { useTypedDispatch, useTypedSelector } from "../../../store/store";
import MODAL from "../../../utils/constants/modal";
import SvgButton from "../../shared/SvgButton";

import styles from "./index.module.scss";
import CancelBookingModal from "./modals/CancelBookingModal";
import CancellationPolicyModal from "./modals/CancellationPolicyModal";
import DeckPlansModal from "./modals/DeckPlansModal";
import EditCabinModal from "./modals/EditCabinModal";
import ImagesCarouselModal from "./modals/ImagesCarouselModal";
import ItineraryModal from "./modals/ItineraryModal";
import PaymentModal from "./modals/PaymentModal";
import StateroomsModal from "./modals/StateroomsModal";
import SwitchAgentModal from "./modals/SwitchAgentModal";

const MODALS_BY_TYPE = {
  [MODAL.MODAL_TYPES.ITINERARY]: ItineraryModal,
  [MODAL.MODAL_TYPES.IMAGES]: ImagesCarouselModal,
  [MODAL.MODAL_TYPES.STATEROOMS]: StateroomsModal,
  [MODAL.MODAL_TYPES.DECK_PLANS]: DeckPlansModal,
  [MODAL.MODAL_TYPES.CANCEL_BOOKING]: CancelBookingModal,
  [MODAL.MODAL_TYPES.CANCELLATION_POLICY]: CancellationPolicyModal,
  [MODAL.MODAL_TYPES.PAYMENT]: PaymentModal,
  [MODAL.MODAL_TYPES.EDIT_CABIN]: EditCabinModal,
  [MODAL.MODAL_TYPES.SWITCH_AGENT]: SwitchAgentModal,
};

function Modal({ type }: { type: string }) {
  const Component = MODALS_BY_TYPE[type] ?? "div";

  return <Component />;
}

function ModalContainer({ children }: React.PropsWithChildren) {
  const dispatch = useTypedDispatch();
  const { savePosition, restorePosition } = useClientPosition();

  const { type, size, title } = useTypedSelector((state) => state.modal);

  const [internalType, setInternalType] = useState(type);
  const [internalTitle, setInternalTitle] = useState(title);

  const outsideRef = useRef(null);

  const handleCloseModal = () => {
    if (internalType) {
      dispatch(setModalType(""));
      restorePosition();

      setTimeout(() => {
        dispatch(hideModal());
      }, 500);
    }
  };

  useOnClickOutside(outsideRef, handleCloseModal);

  useEffect(() => {
    if (type) {
      savePosition();
    }

    setTimeout(
      () => {
        setInternalType(type);
        setInternalTitle(title ?? type);
      },
      type ? 0 : 300,
    );
  }, [type]);

  return (
    <div
      className={classNames(styles.container, {
        [styles.container_active]: internalType,
      })}>
      {children}

      <div
        className={classNames(styles.modal, {
          [styles.modal_active]: internalType,
        })}>
        <div
          ref={outsideRef}
          className={classNames(styles.content, {
            [styles.content_active]: type,
            [styles.content_sm]: size === "sm" || !size,
            [styles.content_md]: size === "md",
            [styles.content_lg]: size === "lg",
          })}>
          <div className={styles.header}>
            <span className={styles.headerTitle}>{internalTitle}</span>

            <SvgButton
              icon="FiX"
              className={styles.closeIcon}
              onClick={handleCloseModal}
            />
          </div>

          <Modal type={internalType} />
        </div>
      </div>
    </div>
  );
}

export default ModalContainer;
